import * as React from "react"
import { AppSidebar } from "@/components/ui/app-sidebar"
import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import {
  HelpOverlayProvider,
  HelpOverlayTrigger,
  HelpOverlay,
} from "@/components/ui/help-overlay"
import {
  ContactOverlayProvider,
  ContactOverlay,
} from "@/components/ui/contact-overlay"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { LoadingDots } from "@/components/ui/loading-dots"
import { RBACProvider } from './rbac-context';
import { useEnhancedRBACContext } from './useEnhancedRBACContext';
import { useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"
import { Toaster } from "@/components/ui/sonner"
import { toast } from "sonner"
import { useOrganizationSelectionWithRedirect } from "@/hooks/useOrganizationSelection"
import { Org } from "@/types/dynamodb-tables"

interface RBACMainLayoutProps {
  children: React.ReactNode
}

// Component to display organization info when selection is disabled
const OrganizationDisplay = ({ 
  organization, 
  placeholder,
  className = ""
}: { 
  organization: Org | null, 
  placeholder: string,
  className?: string
}) => {
  if (!organization) {
    return (
      <div className={cn("w-[260px] px-3 py-2 text-sm text-muted-foreground", className)}>
        {placeholder}
      </div>
    )
  }

  // If organization has a logo, display logo on left and name on right
  if (organization.logo) {
    return (
      <div className={cn("w-[260px] flex items-center gap-2 px-3 py-2", className)}>
        <img 
          src={organization.logo} 
          alt={organization.name || 'Organization logo'} 
          className="h-8 w-auto max-w-[80px] object-contain flex-shrink-0"
          onError={(e) => {
            // Fallback to name only if logo fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            target.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <span className="truncate text-sm font-medium hidden">
          {organization.name || placeholder}
        </span>
        <span className="truncate text-sm font-medium flex-1">
          {organization.name || placeholder}
        </span>
      </div>
    )
  }

  // Otherwise display the name
  return (
    <div className={cn("w-[260px] px-3 py-2 text-sm font-medium", className)}>
      {organization.name || placeholder}
    </div>
  )
}

// Memoized Combobox component for districts with RBAC
const RBACDistrictCombobox = ({ 
  districts, 
  selectedDistrict, 
  onDistrictChange, 
  showWarning,
  placeholder,
  disabled = false,
  isLoading = false,
  userRole
}: { 
  districts: Org[], 
  selectedDistrict: Org | null, 
  onDistrictChange: (orgId: string) => void, 
  showWarning: boolean,
  placeholder: string,
  disabled?: boolean,
  isLoading?: boolean,
  userRole: string | null
}) => {
  const [open, setOpen] = React.useState(false)
  
  // Check if disabled due to role restrictions (should show display component)
  const isDisabledByRole = userRole === 'school_champion' || userRole === 'district_champion';
  
  // If disabled by role, show the organization display instead of combobox
  if (isDisabledByRole) {
    return (
      <div className="relative">
        <OrganizationDisplay 
          organization={selectedDistrict} 
          placeholder={placeholder}
        />
        {showWarning && (
          <div className="absolute -bottom-4 left-0 text-xs text-red-500">
            Please select a district first
          </div>
        )}
      </div>
    )
  }
  
  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading}
            className={cn(
              "w-[260px] justify-between",
              showWarning && "border-red-500 focus:ring-red-500",
              (disabled || isLoading) && "opacity-50 cursor-not-allowed bg-gray-50"
            )}
            title={selectedDistrict?.name || placeholder}
          >
            <span className="truncate flex items-center gap-2">
              {isLoading ? (
                <>
                  <LoadingDots size="sm" color="muted" />
                  <span className="text-muted-foreground">Loading...</span>
                </>
              ) : (
                selectedDistrict?.name || placeholder
              )}
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0">
          <Command>
            <CommandInput placeholder="Search districts..." className="focus:ring-0 focus:outline-none" />
            <CommandList>
              <CommandEmpty>No district found.</CommandEmpty>
              <CommandGroup>
                {/* Add clear option for tm_admin */}
                {userRole === 'tm_admin' && selectedDistrict && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onDistrictChange("__clear__")
                      setOpen(false)
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    Clear Selection
                  </CommandItem>
                )}
                {districts
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((district: Org) => (
                    <CommandItem
                      key={district.orgId}
                      value={district.name || ""}
                      onSelect={() => {
                        onDistrictChange(district.orgId || "")
                        setOpen(false)
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedDistrict?.orgId === district.orgId ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {district.name}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showWarning && (
        <div className="absolute -bottom-4 left-0 text-xs text-red-500">
          Please select a district first
        </div>
      )}
    </div>
  )
}

// Memoized Combobox component for schools with RBAC
const RBACSchoolCombobox = ({ 
  schools, 
  selectedSchool, 
  onSchoolChange, 
  isLoading,
  disabled,
  showWarning,
  placeholder,
  userRole
}: { 
  schools: Org[], 
  selectedSchool: Org | null, 
  onSchoolChange: (orgId: string) => void, 
  isLoading: boolean,
  disabled: boolean,
  showWarning: boolean,
  placeholder: string,
  userRole: string | null
}) => {
  const [open, setOpen] = React.useState(false)
  
  // Check if disabled due to role restrictions (should show display component)
  const isDisabledByRole = userRole === 'school_champion';
  
  // If disabled by role, show the organization display instead of combobox
  if (isDisabledByRole) {
    return (
      <div className="relative">
        <OrganizationDisplay 
          organization={selectedSchool} 
          placeholder={placeholder}
        />
        {showWarning && (
          <div className="absolute -bottom-4 left-0 text-xs text-red-500">
            {placeholder}
          </div>
        )}
      </div>
    )
  }
  
  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading || disabled}
            className={cn(
              "w-[260px] justify-between",
              showWarning && "border-red-500 focus:ring-red-500",
              (isLoading || disabled) && "opacity-50 cursor-not-allowed bg-gray-50"
            )}
            title={selectedSchool?.name || placeholder}
          >
            <span className="truncate flex items-center gap-2">
              {isLoading ? (
                <>
                  <LoadingDots size="sm" color="muted" />
                  <span className="text-muted-foreground">Loading...</span>
                </>
              ) : (
                selectedSchool?.name || placeholder
              )}
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0">
          <Command>
            <CommandInput placeholder="Search schools..." className="focus:ring-0 focus:outline-none" />
            <CommandList>
              <CommandEmpty>No school found.</CommandEmpty>
              <CommandGroup>
                {/* Add clear option for tm_admin and district_champion */}
                {(userRole === 'tm_admin' || userRole === 'district_champion') && selectedSchool && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onSchoolChange("__clear__")
                      setOpen(false)
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    Clear Selection
                  </CommandItem>
                )}
                {schools
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((school) => (
                    <CommandItem
                      key={school.orgId}
                      value={school.name || ""}
                      onSelect={() => {
                        onSchoolChange(school.orgId || "")
                        setOpen(false)
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedSchool?.orgId === school.orgId ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {school.name}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showWarning && (
        <div className="absolute -bottom-4 left-0 text-xs text-red-500">
          {placeholder}
        </div>
      )}
    </div>
  )
}

function RBACMainLayoutInner({ children }: RBACMainLayoutProps) {
  const { t } = useTranslation('org');
  const {
    districts,
    schools,
    isLoadingDistricts,
    isLoadingSchools,
    selectedDistrict,
    setSelectedDistrict,
    clearSelectedDistrict,
    selectedSchool,
    setSelectedSchool,
    clearSelectedSchool,
    schoolSelectorWarning,
    defaultDistrict,
    defaultSchool,
    permissions,
  } = useEnhancedRBACContext();
  const location = useLocation();

  // Set default selections when they become available
  React.useEffect(() => {
    if (defaultDistrict && !selectedDistrict) {
      setSelectedDistrict(defaultDistrict);
    }
  }, [defaultDistrict, selectedDistrict, setSelectedDistrict]);

  React.useEffect(() => {
    if (defaultSchool && !selectedSchool) {
      setSelectedSchool(defaultSchool);
    }
  }, [defaultSchool, selectedSchool, setSelectedSchool]);

  // Check if we're on the assessment creation page
  const isAssessmentCreatePage = location.pathname === "/assessment/create";

  // Show warning toasts when on assessment create page without required selections
  React.useEffect(() => {
    if (isAssessmentCreatePage) {
      if (!selectedDistrict) {
        toast.warning(t('selectDistrictWarning'), {
          duration: 5000,
        });
      }
      if (!selectedSchool) {
        toast.warning(t('selectGroupWarning'), {
          duration: 5000,
        });
      }
    }
  }, [isAssessmentCreatePage, selectedDistrict, selectedSchool, t]);

  // Use the enhanced organization selection hook with redirect functionality
  const { handleDistrictChange, handleSchoolChange } = useOrganizationSelectionWithRedirect(
    setSelectedDistrict,
    setSelectedSchool,
    clearSelectedDistrict,
    clearSelectedSchool,
    districts,
    schools
  );

  // Note: Role-based disabled logic is now handled internally in the combobox components

  return (
    <SidebarProvider>
      <HelpOverlayProvider>
        <ContactOverlayProvider>
          <AppSidebar />
          <SidebarInset>
            <header className="flex h-16 shrink-0 items-center gap-2 border-b">
              <div className="flex items-center gap-2 px-3">
                <SidebarTrigger />
                <Separator orientation="vertical" className="mr-2 h-4" />
                <div className="flex items-center gap-4">
                  {/* District Combobox */}
                  <RBACDistrictCombobox
                    districts={districts}
                    selectedDistrict={selectedDistrict}
                    onDistrictChange={handleDistrictChange}
                    showWarning={isAssessmentCreatePage && !selectedDistrict}
                    placeholder={t('selectOrg')}
                    disabled={false} // Role-based disabled is handled internally
                    isLoading={isLoadingDistricts}
                    userRole={permissions.userRole}
                  />
                  {/* School Combobox - show for all users, but make read-only for school_champion */}
                  <RBACSchoolCombobox
                    schools={schools}
                    selectedSchool={selectedSchool}
                    onSchoolChange={handleSchoolChange}
                    isLoading={isLoadingSchools}
                    disabled={!selectedDistrict?.orgId} // Only disable when no district selected, role-based disabled is handled internally
                    showWarning={isAssessmentCreatePage && !selectedSchool || schoolSelectorWarning}
                    placeholder={t('selectGroup')}
                    userRole={permissions.userRole}
                  />
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <HelpOverlayTrigger />
                </div>
              </div>
            </header>
            <main className="flex flex-1 flex-col gap-4 p-4">
              {children}
            </main>
            <Toaster position="bottom-right" />
          </SidebarInset>
          <HelpOverlay />
          <ContactOverlay />
        </ContactOverlayProvider>
      </HelpOverlayProvider>
    </SidebarProvider>
  );
}

export function RBACMainLayout({ children }: RBACMainLayoutProps) {
  return (
    <RBACProvider>
      <RBACMainLayoutInner>
        {children}
      </RBACMainLayoutInner>
    </RBACProvider>
  );
} 